import React, { useState, useEffect, useCallback, useMemo } from 'react'
import apiClient, { API_ENDPOINTS, BASE_URL, getUserInfoSync } from '../../config/api'

const getPreferredBranchId = (userInfo) => {
  if (!userInfo) {
    return null
  }

  if (typeof userInfo.branch === 'number') {
    return userInfo.branch
  }

  if (typeof userInfo.branch === 'object' && userInfo.branch !== null) {
    if (typeof userInfo.branch.id === 'number') {
      return userInfo.branch.id
    }
  }

  return (
    userInfo.branch_id ||
    userInfo.assigned_branch_id ||
    userInfo.branchId ||
    null
  )
}

const getPreferredBranchName = (userInfo) => {
  if (!userInfo) {
    return null
  }

  if (typeof userInfo.branch === 'object' && userInfo.branch !== null) {
    if (typeof userInfo.branch.name === 'string' && userInfo.branch.name.trim()) {
      return userInfo.branch.name
    }
  }

  return userInfo.branch_name || userInfo.branchName || null
}

const Sales = () => {
  const userInfo = getUserInfoSync()
  const preferredBranchId = useMemo(() => getPreferredBranchId(userInfo), [userInfo])
  const preferredBranchName = useMemo(() => getPreferredBranchName(userInfo), [userInfo])

  const [filters, setFilters] = useState({
    search: '',
    dateRange: 'today',
    status: ''
  })

  const [sales, setSales] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [expandedSaleId, setExpandedSaleId] = useState(null)
  const [cashierDirectory, setCashierDirectory] = useState({})
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' })
  const [salesReport, setSalesReport] = useState(null)
  const [reportLoading, setReportLoading] = useState(false)
  const [reportError, setReportError] = useState('')
  const [filtersExpanded, setFiltersExpanded] = useState(false)

  const handleFilterChange = (event) => {
    const { name, value } = event.target
    setFilters((prev) => ({
      ...prev,
      [name]: value
    }))

    if (name === 'dateRange' && value !== 'custom') {
      setCustomDateRange({ start: '', end: '' })
    }

    if (name !== 'search') {
      setExpandedSaleId(null)
    }
  }

  const handleClearFilters = () => {
    setFilters({
      search: '',
      dateRange: 'today',
      status: ''
    })
    setCustomDateRange({ start: '', end: '' })
    setExpandedSaleId(null)
  }

  const handleCustomDateChange = (event) => {
    const { name, value } = event.target
    setCustomDateRange((prev) => ({
      ...prev,
      [name]: value
    }))
    setExpandedSaleId(null)
  }

  const formatCurrency = (value) => {
    const parsed = parseFloat(value)
    if (Number.isNaN(parsed)) {
      return value ?? '-'
    }
    return parsed.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  const formatNumber = (value, { decimals = 0 } = {}) => {
    const parsed = Number(value)
    if (Number.isNaN(parsed)) {
      return '-'
    }
    return parsed.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    })
  }

  const SummaryBadge = ({ icon, label, value, gradient }) => (
    <div
      className="d-flex align-items-center"
      style={{
        background: gradient,
        padding: '6px 14px',
        borderRadius: '20px',
        color: '#fff',
        fontSize: '13px',
        fontWeight: 600,
        boxShadow: '0 2px 8px rgba(15, 0, 40, 0.2)'
      }}
    >
      <i className={`bi ${icon} me-2`} />
      <span>
        {label}: <strong>{value ?? '-'}</strong>
      </span>
    </div>
  )

  const formatDateTime = (value) => {
    if (!value) return '-'
    try {
      return new Date(value).toLocaleString(undefined, {
        dateStyle: 'medium',
        timeStyle: 'short'
      })
    } catch (err) {
      return value
    }
  }

  const getDateRangeFilter = useCallback((rangeKey, customRange) => {
    if (rangeKey === 'all' || rangeKey === '') {
      return () => true
    }

    if (rangeKey === 'custom') {
      const startDate = customRange?.start ? new Date(`${customRange.start}T00:00:00`) : null
      const endDate = customRange?.end ? new Date(`${customRange.end}T23:59:59`) : null

      if (!startDate && !endDate) {
        return () => true
      }

      return (dateString) => {
        if (!dateString) return false
        const saleDate = new Date(dateString)
        if (Number.isNaN(saleDate.valueOf())) return false
        if (startDate && saleDate < startDate) {
          return false
        }
        if (endDate && saleDate > endDate) {
          return false
        }
        return true
      }
    }

    const now = new Date()
    const start = new Date(now)

    switch (rangeKey) {
      case 'today':
        start.setHours(0, 0, 0, 0)
        break
      case '7days':
        start.setDate(now.getDate() - 7)
        break
      case '30days':
        start.setDate(now.getDate() - 30)
        break
      default:
        return () => true
    }

    return (dateString) => {
      if (!dateString) return false
      const saleDate = new Date(dateString)
      return saleDate >= start
    }
  }, [])

  const resolvedDateRange = useMemo(() => {
    const formatDate = (date) => date.toISOString().split('T')[0]
    const today = new Date()

    switch (filters.dateRange) {
      case 'today':
        return { start: formatDate(today), end: formatDate(today) }
      case '7days': {
        const start = new Date(today)
        start.setDate(start.getDate() - 6)
        return { start: formatDate(start), end: formatDate(today) }
      }
      case '30days': {
        const start = new Date(today)
        start.setDate(start.getDate() - 29)
        return { start: formatDate(start), end: formatDate(today) }
      }
      case 'custom':
        if (customDateRange.start && customDateRange.end) {
          return { start: customDateRange.start, end: customDateRange.end }
        }
        return null
      default:
        return null
    }
  }, [filters.dateRange, customDateRange])

  const salesReportParams = useMemo(() => {
    if (!resolvedDateRange) {
      return null
    }

    const params = {
      start_date: resolvedDateRange.start,
      end_date: resolvedDateRange.end
    }

    if (preferredBranchId) {
      params.branch_id = Number(preferredBranchId)
    }

    return params
  }, [resolvedDateRange, preferredBranchId])

  const reportAvailabilityMessage = useMemo(() => {
    if (filters.dateRange === 'all') {
      return 'Select a specific period to load the backend sales summary.'
    }
    if (filters.dateRange === 'custom' && (!customDateRange.start || !customDateRange.end)) {
      return 'Provide both start and end dates for the custom range.'
    }
    return ''
  }, [filters.dateRange, customDateRange])

  const fetchSales = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      const response = await apiClient.get(API_ENDPOINTS.SALES)
      const data = Array.isArray(response.data) ? response.data : response.data?.results || []
      setSales(data)
    } catch (err) {
      console.error('Error fetching sales:', err)
      const message = err.response?.data?.detail || err.response?.data?.message || 'Failed to load sales. Please try again.'
      setError(message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchSales()
  }, [fetchSales])

  const fetchCashiers = useCallback(async () => {
    try {
      const response = await apiClient.get(`${BASE_URL}/api/accounts/users/`)
      const directory = Array.isArray(response.data)
        ? response.data.reduce((acc, user) => {
            if (user?.user?.id) {
              acc[user.user.id] = {
                name: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.user.email || `User ${user.user.id}`,
                email: user.user.email,
                code: user.code
              }
            }
            return acc
          }, {})
        : {}
      setCashierDirectory(directory)
    } catch (err) {
      console.warn('Unable to fetch cashier directory:', err)
      setCashierDirectory({})
    }
  }, [])

  useEffect(() => {
    fetchCashiers()
  }, [fetchCashiers])

  useEffect(() => {
    if (!salesReportParams) {
      setSalesReport(null)
      setReportError('')
      setReportLoading(false)
      return
    }

    let ignore = false

    const fetchSalesReport = async () => {
      try {
        setReportLoading(true)
        setReportError('')
        const response = await apiClient.get(API_ENDPOINTS.SALES_REPORT, {
          params: {
            ...salesReportParams,
            group_by: 'day'
          }
        })
        if (ignore) {
          return
        }
        setSalesReport(response.data || null)
      } catch (err) {
        if (ignore) {
          return
        }
        console.error('Error fetching sales report:', err)
        const message =
          err.response?.data?.detail ||
          err.response?.data?.message ||
          'Failed to load sales report summary. Please try again.'
        setReportError(message)
        setSalesReport(null)
      } finally {
        if (!ignore) {
          setReportLoading(false)
        }
      }
    }

    fetchSalesReport()

    return () => {
      ignore = true
    }
  }, [salesReportParams])

  const getCashierDisplay = (sale) => {
    const info = cashierDirectory[sale.cashier]
    if (info) {
      return {
        name: info.name,
        email: info.email,
        code: info.code
      }
    }
    const fallbackName = sale.cashier_email?.split('@')?.[0] || `Cashier ${sale.cashier}`
    return {
      name: fallbackName,
      email: sale.cashier_email,
      code: null
    }
  }

  const filteredSales = useMemo(() => {
    const matchesDate = getDateRangeFilter(filters.dateRange, customDateRange)

    return sales.filter((sale) => {
      const searchTerm = filters.search.trim().toLowerCase()
      if (searchTerm) {
        const haystack = [
          sale.sale_number,
          sale.branch_name,
          sale.cashier_email,
          sale.type_of_payment,
          sale.notes
        ]
          .filter(Boolean)
          .join(' ')
          .toLowerCase()

        if (!haystack.includes(searchTerm)) {
          return false
        }
      }

      if (filters.status && sale.status?.toLowerCase() !== filters.status.toLowerCase()) {
        return false
      }

      if (!matchesDate(sale.created_at)) {
        return false
      }

      return true
    })
  }, [sales, filters, customDateRange, getDateRangeFilter])

  const toggleExpand = (saleId) => {
    setExpandedSaleId((prev) => (prev === saleId ? null : saleId))
  }

  return (
    <>
      <div className="pagetitle">
        <h1>Sales Records</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item">Sales</li>
            <li className="breadcrumb-item active">Sales Records</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                <div className="border rounded-3 p-3 mb-4"
                  style={{
                    background: '#f8fafc',
                    border: '1px solid #e0e6ed',
                    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)'
                  }}
                >
                  <div
                    className="d-flex justify-content-between align-items-center flex-wrap mb-3"
                    style={{ paddingBottom: '15px', borderBottom: '2px solid #e9ecef' }}
                  >
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                      <h5 className="mb-0">
                        <i className="bi bi-graph-up-arrow me-2" />
                        Sales Overview
                      </h5>
                      {salesReport && (
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                          <SummaryBadge
                            icon="bi-receipt"
                            label="Orders"
                            value={formatNumber(salesReport.summary?.total_orders)}
                            gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                          />
                          <SummaryBadge
                            icon="bi-box-seam"
                            label="Units"
                            value={formatNumber(salesReport.summary?.total_units_sold, { decimals: 2 })}
                            gradient="linear-gradient(135deg, #11998e 0%, #38ef7d 100%)"
                          />
                          <SummaryBadge
                            icon="bi-currency-dollar"
                            label="Sales"
                            value={formatCurrency(salesReport.summary?.total_sales)}
                            gradient="linear-gradient(135deg, #f6d365 0%, #fda085 100%)"
                          />
                          <SummaryBadge
                            icon="bi-graph-up"
                            label="Avg Order"
                            value={formatCurrency(salesReport.summary?.average_order_value)}
                            gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)"
                          />
                        </div>
                      )}
                    </div>
                    <small className="text-muted">
                      {salesReportParams
                        ? `Range: ${salesReportParams.start_date} → ${salesReportParams.end_date} • ${
                            preferredBranchName || 'All branches'
                          }`
                        : reportAvailabilityMessage || 'Choose a supported date range to load the backend sales report.'}
                    </small>
                  </div>

                  {!salesReportParams ? (
                    <div className="alert alert-info mb-0" role="alert">
                      {reportAvailabilityMessage || 'Select a specific period to view the backend summary.'}
                    </div>
                  ) : reportLoading ? (
                    <div className="d-flex align-items-center text-muted">
                      <div className="spinner-border text-primary me-2" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                      Fetching sales report summary...
                    </div>
                  ) : reportError ? (
                    <div className="alert alert-danger mb-0" role="alert">
                      {reportError}
                    </div>
                  ) : salesReport ? (
                    <>
                      {/* <div>
                        <h6 className="text-uppercase text-muted small mb-2">Daily breakdown</h6>
                        {Array.isArray(salesReport.breakdown) && salesReport.breakdown.length > 0 ? (
                          <div className="table-responsive small">
                            <table className="table table-sm align-middle mb-0">
                              <thead>
                                <tr>
                                  <th>Date</th>
                                  <th className="text-end">Sales</th>
                                  <th className="text-end">Units</th>
                                </tr>
                              </thead>
                              <tbody>
                                {salesReport.breakdown.map((entry, index) => (
                                  <tr key={entry.label || index}>
                                    <td>{entry.label || '-'}</td>
                                    <td className="text-end">{formatCurrency(entry.total_sales)}</td>
                                    <td className="text-end">{formatNumber(entry.total_units, { decimals: 2 })}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <p className="text-muted mb-0">No breakdown data returned for this selection.</p>
                        )}
                      </div> */}
                    </>
                  ) : (
                    <p className="text-muted mb-0">No sales summary returned for this range.</p>
                  )}
                </div>

                <div
                  className="border rounded-3 p-3 mb-4"
                  style={{
                    background: '#fdfdfd',
                    border: '1px solid #e9ecef',
                    boxShadow: '0 2px 12px rgba(15, 23, 42, 0.05)'
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
                    <div className="d-flex align-items-center gap-2">
                      <h6 className="mb-0">Filters</h6>
                      <span className="badge bg-light text-dark">{filtersExpanded ? 'Expanded' : 'Hidden'}</span>
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        className="btn btn-sm btn-outline-secondary"
                        type="button"
                        onClick={() => setFiltersExpanded((prev) => !prev)}
                      >
                        <i className={`bi bi-${filtersExpanded ? 'chevron-up' : 'chevron-down'} me-1`} />
                        {filtersExpanded ? 'Hide Filters' : 'Show Filters'}
                      </button>
                      <button className="btn btn-outline-secondary btn-sm" onClick={handleClearFilters}>
                        Reset
                      </button>
                    </div>
                  </div>
                  {!filtersExpanded && (
                    <div className="text-muted small">
                      Showing results for <strong>{filters.dateRange === 'today' ? 'Today' : 'Custom Range'}</strong>
                      {filters.search && (
                        <>
                          {' '}• Search: <strong>{filters.search}</strong>
                        </>
                      )}
                      {filters.status && (
                        <>
                          {' '}• Status: <strong className="text-capitalize">{filters.status}</strong>
                        </>
                      )}
                    </div>
                  )}
                  {filtersExpanded && (
                    <>
                      <div className="row g-3">
                        <div className="col-sm-6 col-lg-3 col-xxl-2">
                          <label className="form-label">Search</label>
                          <input
                            type="text"
                            className="form-control"
                            name="search"
                            value={filters.search}
                            onChange={handleFilterChange}
                            placeholder="Invoice, customer, SKU..."
                          />
                        </div>
                        <div className="col-sm-6 col-lg-3 col-xxl-2">
                          <label className="form-label">Date Range</label>
                          <select
                            className="form-select"
                            name="dateRange"
                            value={filters.dateRange}
                            onChange={handleFilterChange}
                          >
                            <option value="today">Today</option>
                            <option value="custom">Custom Date Range</option>
                          </select>
                        </div>
                        <div className="col-sm-6 col-lg-3 col-xxl-2">
                          <label className="form-label">Status</label>
                          <select
                            className="form-select"
                            name="status"
                            value={filters.status}
                            onChange={handleFilterChange}
                          >
                            <option value="">All</option>
                            <option value="completed">Completed</option>
                            <option value="pending">Pending</option>
                            <option value="processing">Processing</option>
                            <option value="cancelled">Cancelled</option>
                          </select>
                        </div>
                        {filters.dateRange === 'custom' && (
                          <>
                            <div className="col-sm-6 col-lg-3 col-xxl-2">
                              <label className="form-label">Start date</label>
                              <input
                                type="date"
                                className="form-control"
                                name="start"
                                value={customDateRange.start}
                                max={customDateRange.end || undefined}
                                onChange={handleCustomDateChange}
                              />
                            </div>
                            <div className="col-sm-6 col-lg-3 col-xxl-2">
                              <label className="form-label">End date</label>
                              <input
                                type="date"
                                className="form-control"
                                name="end"
                                value={customDateRange.end}
                                min={customDateRange.start || undefined}
                                onChange={handleCustomDateChange}
                              />
                            </div>
                          </>
                        )}
                      </div>
                      {filters.dateRange === 'custom' && (
                        <div className="mt-2 text-muted small">
                          Select start and end dates to narrow down the results. Leaving either field empty makes the filter open-ended.
                        </div>
                      )}
                    </>
                  )}
                </div>

                {error && (
                  <div className="alert alert-danger" role="alert">
                    {error}
                  </div>
                )}

                <div className="table-responsive">
                  <table className="table table-hover align-middle">
                    <thead>
                      <tr>
                        <th style={{ width: '40px' }} />
                        <th scope="col">Sale #</th>
                        <th scope="col">Branch</th>
                        <th scope="col">Cashier</th>
                        <th scope="col">Payment</th>
                        <th scope="col" className="text-end">Total</th>
                        <th scope="col">Status</th>
                        <th scope="col">Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading && (
                        <tr>
                          <td colSpan="8" className="text-center py-4">
                            <div className="spinner-border text-primary me-2" role="status">
                              <span className="visually-hidden">Loading...</span>
                            </div>
                            Fetching latest sales...
                          </td>
                        </tr>
                      )}
                      {!loading && filteredSales.length === 0 && (
                        <tr>
                          <td colSpan="8" className="text-center py-4 text-muted">
                            No sales match the current filters.
                          </td>
                        </tr>
                      )}
                      {!loading &&
                        filteredSales.map((sale) => {
                          const isExpanded = expandedSaleId === sale.id
                          return (
                            <React.Fragment key={sale.id}>
                              <tr
                                onClick={() => toggleExpand(sale.id)}
                                style={{ cursor: 'pointer' }}
                                className={isExpanded ? 'table-active' : ''}
                              >
                                <td>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={(event) => {
                                      event.stopPropagation()
                                      toggleExpand(sale.id)
                                    }}
                                  >
                                    <i className={`bi bi-chevron-${isExpanded ? 'up' : 'down'}`} />
                                  </button>
                                </td>
                                <td className="fw-semibold">{sale.sale_number || '-'}</td>
                                <td>{sale.branch_name || '-'}</td>
                                <td>
                                  <div className="d-flex flex-column">
                                    <span className="fw-semibold">{getCashierDisplay(sale).name}</span>
                                    {getCashierDisplay(sale).email && (
                                      <small className="text-muted">{getCashierDisplay(sale).email}</small>
                                    )}
                                  </div>
                                </td>
                                <td className="text-capitalize">{sale.type_of_payment || '-'}</td>
                                <td className="text-end fw-semibold">{formatCurrency(sale.total_amount)}</td>
                                <td>
                                  <span className={`badge bg-${sale.status === 'completed' ? 'success' : sale.status === 'pending' ? 'warning' : sale.status === 'cancelled' ? 'danger' : 'secondary'}`}>
                                    {sale.status || 'Unknown'}
                                  </span>
                                </td>
                                <td>{formatDateTime(sale.created_at)}</td>
                              </tr>
                              {isExpanded && (
                                <tr className="bg-light-subtle">
                                  <td colSpan="8">
                                    <div className="p-3 border rounded-3 bg-white shadow-sm">
                                      <div className="row g-4">
                                        <div className="col-lg-4">
                                          <div className="h-100">
                                            <h6 className="text-uppercase text-muted small mb-3">Snapshot</h6>
                                            <div className="mb-3">
                                              <p className="mb-1 text-muted small">Cashier</p>
                                              <p className="mb-0 fw-semibold">{getCashierDisplay(sale).name}</p>
                                              {getCashierDisplay(sale).email && (
                                                <small className="text-muted">{getCashierDisplay(sale).email}</small>
                                              )}
                                              {getCashierDisplay(sale).code && (
                                                <small className="d-block text-muted">Code: {getCashierDisplay(sale).code}</small>
                                              )}
                                            </div>
                                            <div className="mb-3">
                                              <p className="mb-1 text-muted small">Branch</p>
                                              <p className="mb-0">{sale.branch_name || '-'}</p>
                                            </div>
                                            <div className="mb-3">
                                              <p className="mb-1 text-muted small">Payment Method</p>
                                              <span className="badge bg-light text-dark text-capitalize">{sale.type_of_payment}</span>
                                            </div>
                                            <div className="mb-3">
                                              <p className="mb-1 text-muted small">Created</p>
                                              <p className="mb-0">{formatDateTime(sale.created_at)}</p>
                                            </div>
                                            <div>
                                              <p className="mb-1 text-muted small">Financial Summary</p>
                                              <div className="border rounded-3 p-2">
                                                <div className="d-flex justify-content-between small">
                                                  <span>Subtotal</span>
                                                  <strong>{formatCurrency(sale.total_amount)}</strong>
                                                </div>
                                                <div className="d-flex justify-content-between small">
                                                  <span>Discount</span>
                                                  <strong>{formatCurrency(sale.discount)}</strong>
                                                </div>
                                                <div className="d-flex justify-content-between small">
                                                  <span>Tax</span>
                                                  <strong>{formatCurrency(sale.tax)}</strong>
                                                </div>
                                                <div className="d-flex justify-content-between small">
                                                  <span>Net</span>
                                                  <strong>{formatCurrency(sale.net_amount)}</strong>
                                                </div>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                        <div className="col-lg-8">
                                          <h6 className="text-uppercase text-muted small mb-3">Items</h6>
                                          {sale.items?.length > 0 ? (
                                            <div className="table-responsive small">
                                              <table className="table table-sm align-middle mb-0">
                                                <thead>
                                                  <tr>
                                                    <th>Product</th>
                                                    <th className="text-end">Qty</th>
                                                    <th className="text-end">Unit</th>
                                                    <th className="text-end">Subtotal</th>
                                                    <th className="text-end">Profit</th>
                                                  </tr>
                                                </thead>
                                                <tbody>
                                                  {sale.items.map((item) => (
                                                    <tr key={item.id}>
                                                      <td>
                                                        <div className="d-flex flex-column">
                                                          <span>{item.product_name}</span>
                                                          <small className="text-muted">{item.product_sku}</small>
                                                        </div>
                                                      </td>
                                                      <td className="text-end">{item.quantity}</td>
                                                      <td className="text-end">{formatCurrency(item.unit_price)}</td>
                                                      <td className="text-end">{formatCurrency(item.subtotal)}</td>
                                                      <td className="text-end">{formatCurrency(item.profit)}</td>
                                                    </tr>
                                                  ))}
                                                </tbody>
                                              </table>
                                            </div>
                                          ) : (
                                            <p className="text-muted mb-0">No line items recorded.</p>
                                          )}

                                          {Array.isArray(sale.returns) && sale.returns.length > 0 && (
                                            <div className="mt-4">
                                              <h6 className="text-uppercase text-muted small mb-2">Returns</h6>
                                              <div className="table-responsive small">
                                                <table className="table table-sm mb-0">
                                                  <thead>
                                                    <tr>
                                                      <th>Product</th>
                                                      <th className="text-end">Qty</th>
                                                      <th className="text-end">Amount</th>
                                                    </tr>
                                                  </thead>
                                                  <tbody>
                                                    {sale.returns.map((returnItem) => (
                                                      <tr key={returnItem.id}>
                                                        <td>{returnItem.product_name || '-'}</td>
                                                        <td className="text-end">{returnItem.quantity || '-'}</td>
                                                        <td className="text-end">{formatCurrency(returnItem.amount)}</td>
                                                      </tr>
                                                    ))}
                                                  </tbody>
                                                </table>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          )
                        })}
                    </tbody>
                  </table>
                </div>

                <div className="d-flex justify-content-between align-items-center mt-3">
                  <button className="btn btn-outline-primary btn-sm" onClick={fetchSales} disabled={loading}>
                    <i className="bi bi-arrow-clockwise me-1" />
                    Refresh
                  </button>
                  <small className="text-muted">Showing {filteredSales.length} of {sales.length} total records</small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default Sales

